// Is the signed-in person a member of a business somebody pays for? See
// paidBusinessStore for why the question exists and what "pays" means.
//
// Only callers that need it for a user WITHOUT Pro should ask: it costs one
// request per sign-in, and a Pro subscriber never meets the paywall it feeds.

import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  ensureInPaidBusinessLoaded,
  getInPaidBusiness,
  getOwnsBusiness,
  getReadsUnpaidBusiness,
  OwnsBusinessAnswer,
  ReadsUnpaidBusinessAnswer,
  subscribeInPaidBusiness,
} from '../services/paidBusinessStore';

/**
 * True or false once known; null while still being asked. `enabled: false`
 * answers false without asking, for a caller that renders for everyone but
 * needs the answer only for users without Pro.
 */
export function useInPaidBusiness(enabled = true): boolean | null {
  const { user } = useAuth();
  const uuid = user?.uuid;
  const authToken = enabled ? user?.authToken : undefined;

  const [answer, setAnswer] = useState<boolean | null>(() => getInPaidBusiness(uuid));

  useEffect(() => {
    if (!authToken) return undefined;
    const unsubscribe = subscribeInPaidBusiness(setAnswer);
    ensureInPaidBusinessLoaded(uuid);
    // A settled answer from an earlier mount: adopt it rather than waiting
    // for the next change to be published.
    setAnswer(getInPaidBusiness(uuid));
    return unsubscribe;
  }, [authToken, uuid]);

  // No session, so no membership and nothing to wait for. Without this,
  // "signed out" and "still asking" would be the same null.
  if (!authToken) return false;
  return answer;
}

/**
 * Does the signed-in person own a business, and which of theirs is unpaid?
 * (Dave's decision 11, 2026-10-09: an owner without Pro reaches the billing
 * card.) Read off the same request as useInPaidBusiness. `ownsBusiness` is
 * null while still being asked; `enabled: false` answers "owns nothing"
 * without asking.
 */
export function useOwnsBusiness(enabled = true): OwnsBusinessAnswer {
  const { user } = useAuth();
  const uuid = user?.uuid;
  const authToken = enabled ? user?.authToken : undefined;

  const [answer, setAnswer] = useState<OwnsBusinessAnswer>(() => getOwnsBusiness(uuid));

  useEffect(() => {
    if (!authToken) return undefined;
    const unsubscribe = subscribeInPaidBusiness(() => setAnswer(getOwnsBusiness(uuid)));
    ensureInPaidBusinessLoaded(uuid);
    setAnswer(getOwnsBusiness(uuid));
    return unsubscribe;
  }, [authToken, uuid]);

  if (!authToken) return { ownsBusiness: false, unpaidOwnedBusinessId: null };
  return answer;
}

/**
 * Is the signed-in person a clerk or read-only member of a business nobody
 * pays for, and which one? (Gap G46: they keep reading it.) Same request
 * again. `readsUnpaidBusiness` is null while still being asked; `enabled:
 * false` answers no without asking.
 */
export function useReadsUnpaidBusiness(enabled = true): ReadsUnpaidBusinessAnswer {
  const { user } = useAuth();
  const uuid = user?.uuid;
  const authToken = enabled ? user?.authToken : undefined;

  const [answer, setAnswer] = useState<ReadsUnpaidBusinessAnswer>(() => getReadsUnpaidBusiness(uuid));

  useEffect(() => {
    if (!authToken) return undefined;
    const unsubscribe = subscribeInPaidBusiness(() => setAnswer(getReadsUnpaidBusiness(uuid)));
    ensureInPaidBusinessLoaded(uuid);
    setAnswer(getReadsUnpaidBusiness(uuid));
    return unsubscribe;
  }, [authToken, uuid]);

  if (!authToken) return { readsUnpaidBusiness: false, unpaidReadBusinessId: null };
  return answer;
}
